import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AUTH } from "@/constants/testIds";
import { RestoreBackupForm } from "@/components/RestoreBackupForm";
import { LoginWaves } from "@/components/LoginWaves";

export default function Login() {
  const { login } = useAuth();
  const { theme } = useTheme();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [mode, setMode] = useState("login");
  const [rEmail, setREmail] = useState("");
  const [rPass1, setRPass1] = useState("");
  const [rPass2, setRPass2] = useState("");
  const [rError, setRError] = useState("");
  const [rLoading, setRLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    setRError("");
    if (rPass1.length < 6) {
      setRError("La password deve avere almeno 6 caratteri");
      return;
    }
    if (rPass1 !== rPass2) {
      setRError("Le due password non coincidono");
      return;
    }
    setRLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: rEmail.trim(), new_password: rPass1 });
      toast.success("Password aggiornata. Ora puoi accedere con la nuova password.");
      setEmail(rEmail.trim());
      setPassword("");
      setRPass1("");
      setRPass2("");
      setMode("login");
    } catch (err) {
      setRError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setRLoading(false);
    }
  };

  const loginBg = theme?.login_bg || "#cc0033";
  const loginText = theme?.login_text || "#ffffff";

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-6 relative overflow-hidden"
      style={{ backgroundColor: loginBg, color: loginText }}
      data-testid="login-page"
    >
      <LoginWaves color={loginText} />
      <div className="w-full max-w-sm flex flex-col items-center relative z-10">
        <div className="mb-16 flex flex-col items-center" data-testid="login-logo-wrap">
          {theme?.logo ? (
            <img src={theme.logo} alt="Logo" className="h-20 w-auto max-w-[240px] object-contain" data-testid="login-logo" />
          ) : (
            <span className="font-display font-black text-4xl uppercase tracking-tight" style={{ color: loginText }}>
              Sensofon
            </span>
          )}
        </div>

        {mode === "restore" ? (
          <RestoreBackupForm
            onBack={() => setMode("login")}
            onDone={(adminEmail) => { setEmail(adminEmail); setPassword(""); setMode("login"); }}
          />
        ) : (
        <form
          onSubmit={mode === "login" ? submit : resetPassword}
          className="w-full login-card p-8 text-[#09090b]"
          data-testid="login-card"
        >
          <div className="text-center mb-5">
            <span className="font-display font-black text-xl uppercase tracking-tight" data-testid="login-wordmark">
              Activity App
            </span>
            <div className="data-label mt-1" data-testid="login-version">
              Version {theme?.app_version || "1.0"}
            </div>
          </div>

          {mode === "login" ? (
            <>
              <h2 className="font-display font-bold text-2xl tracking-tight">Accedi</h2>
              <p className="data-label mt-2 mb-6">Area riservata</p>

              <label className="data-label block mb-1">Email</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nome@azienda.it"
                className="mb-4"
                required
                data-testid={AUTH.emailInput}
              />
              <label className="data-label block mb-1">Password</label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mb-2"
                required
                data-testid={AUTH.passwordInput}
              />
              <button
                type="button"
                onClick={() => { setMode("reset"); setRError(""); setREmail(email); }}
                className="text-xs text-[#cc0033] hover:underline mb-5 block"
                data-testid="forgot-password-link"
              >
                Password dimenticata?
              </button>

              {error && (
                <p className="text-destructive text-sm mb-4 font-medium" data-testid={AUTH.errorMessage}>
                  {error}
                </p>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#cc0033] text-white hover:bg-[#a30028]"
                data-testid={AUTH.submitButton}
              >
                {loading ? "Accesso…" : "Entra"}
              </Button>
              <button
                type="button"
                onClick={() => { setMode("restore"); setError(""); }}
                className="text-xs text-zinc-500 hover:underline mt-4 block mx-auto"
                data-testid="restore-backup-link"
              >
                Ripristina da backup
              </button>
            </>
          ) : (
            <>
              <h2 className="font-display font-bold text-2xl tracking-tight">Reimposta password</h2>
              <p className="data-label mt-2 mb-6">Inserisci la tua email e la nuova password</p>

              <label className="data-label block mb-1">Email</label>
              <Input
                type="email"
                value={rEmail}
                onChange={(e) => setREmail(e.target.value)}
                placeholder="nome@azienda.it"
                className="mb-4"
                required
                data-testid="reset-email-input"
              />
              <label className="data-label block mb-1">Nuova password</label>
              <Input
                type="password"
                value={rPass1}
                onChange={(e) => setRPass1(e.target.value)}
                placeholder="Almeno 6 caratteri"
                className="mb-4"
                required
                data-testid="reset-password-input"
              />
              <label className="data-label block mb-1">Ripeti nuova password</label>
              <Input
                type="password"
                value={rPass2}
                onChange={(e) => setRPass2(e.target.value)}
                placeholder="Ripeti la password"
                className="mb-5"
                required
                data-testid="reset-password-confirm-input"
              />

              {rError && (
                <p className="text-destructive text-sm mb-4 font-medium" data-testid="reset-error-message">
                  {rError}
                </p>
              )}

              <Button
                type="submit"
                disabled={rLoading}
                className="w-full bg-[#cc0033] text-white hover:bg-[#a30028]"
                data-testid="reset-submit-button"
              >
                {rLoading ? "Salvataggio…" : "Conferma nuova password"}
              </Button>
              <button
                type="button"
                onClick={() => { setMode("login"); setRError(""); }}
                className="text-xs text-zinc-500 hover:underline mt-4 block mx-auto"
                data-testid="back-to-login-link"
              >
                ← Torna al login
              </button>
            </>
          )}
        </form>
        )}
      </div>

      <div
        className="mt-10 text-center text-[10px] leading-relaxed w-full max-w-md sm:max-w-3xl px-4 opacity-80 relative z-10"
        style={{ color: loginText }}
        data-testid="login-footer-legal"
      >
        <div className="hidden sm:block">
          <p>App di proprietà di Sensofon SrlU - P.iva 03502501202</p>
          <p>Made by ICON Agenzia di Comunicazione - P.Iva 01451110777, via Cosenza 53 - 75100 Matera</p>
        </div>
        <div className="sm:hidden space-y-0.5">
          <p>App di proprietà di Sensofon SrlU</p>
          <p>P.iva 03502501202</p>
          <p>Made by ICON Agenzia di Comunicazione - P.Iva 01451110777</p>
          <p>via Cosenza 53 - 75100 Matera</p>
        </div>
      </div>
    </div>
  );
}
